import { FC } from 'react';
import { SimpleExpression, SimpleBinaryExpression, ExpressionItem, LogicalOperator, FieldMetadata, OperatorInfo } from '../../models/expression';
import { SimpleExpressionForm } from './SimpleExpressionForm';
import { LogicalOperatorToggle } from './LogicalOperatorToggle';
import { Button } from 'primereact/button';
import { SelectButton } from 'primereact/selectbutton';
import styles from './RuleGroup.module.css';

interface RuleGroupProps {
  group: SimpleExpression;
  level: number;
  onUpdate: (group: SimpleExpression) => void;
  fieldMetadata?: FieldMetadata[];
  rightFieldMetadata?: FieldMetadata[];
  operators?: OperatorInfo[];
  allowCustomFields?: boolean;
}

export const RuleGroup: FC<RuleGroupProps> = ({ group, level, onUpdate, fieldMetadata, rightFieldMetadata, operators, allowCustomFields }) => {
  const expressions = group.expressions || [];
  const hasMultipleExpressions = expressions.length > 1;

  const hasIncompleteExpression =
    expressions.length === 0 ||
    expressions.some(
      (item) =>
        !item.expressionGroup &&
        (!item.expression || !item.expression.left || !item.expression.operator || !item.expression.right)
    );

  const handleLogicalOperatorChange = (value: LogicalOperator) => {
    onUpdate({
      ...group,
      logicalOperator: value,
    });
  };

  const handleIsNotChange = (value: boolean) => {
    onUpdate({
      ...group,
      isNot: value,
    });
  };

  const handleUpdateExpression = (index: number, expression: SimpleBinaryExpression | null) => {

    if (!expression) {  
      return;
    }
    
    const updatedExpressions = [...expressions];
    
    // Check if the expression actually changed to avoid unnecessary updates
    const existingExpression = updatedExpressions[index]?.expression;
    if (
      existingExpression &&
      existingExpression.left === expression.left &&
      existingExpression.operator === expression.operator &&
      JSON.stringify(existingExpression.right) === JSON.stringify(expression.right) &&
      (existingExpression.isNot ?? false) === (expression.isNot ?? false)
    ) {  
      return; // No change, don't update
    }
    
    if (index >= updatedExpressions.length) {  
      updatedExpressions.push({
        expression,
        expressionGroup: null,
      });
    } else {  
      updatedExpressions[index] = {
        ...updatedExpressions[index],
        expression,
      };
    }

    const updatedGroup = {
      ...group,
      expressions: updatedExpressions,
    };
    onUpdate(updatedGroup);
  };

  const handleDeleteExpression = (index: number) => {
    const updatedExpressions = expressions.filter((_, i) => i !== index);
    onUpdate({
      ...group,
      // Group-level NOT only has a UI control while the group has multiple conditions,
      // so clear it when the group shrinks below two to avoid an unreachable flag.
      isNot: updatedExpressions.length > 1 ? group.isNot : false,
      expressions: updatedExpressions.length > 0 ? updatedExpressions : null,
    });
  };

  const handleCreateNestedGroup = (index: number) => {
    const currentItem = expressions[index];
    if (!currentItem.expression) return;

    const nestedGroup: ExpressionItem = {
      expression: null,
      expressionGroup: {
        expressions: [
          {
            expression: currentItem.expression,
            expressionGroup: null,
          },
          {
            expression: null,
            expressionGroup: null,
          },
        ],
        logicalOperator: group.logicalOperator === LogicalOperator.And ? LogicalOperator.Or : LogicalOperator.And,
        isNot: false,
      },
    };

    const updatedExpressions = [...expressions];
    updatedExpressions[index] = nestedGroup;

    onUpdate({
      ...group,
      expressions: updatedExpressions,
    });
  };

  const handleAddRule = () => {
    const newItem: ExpressionItem = {
      expression: null,
      expressionGroup: null,
    };

    onUpdate({
      ...group,
      expressions: [...expressions, newItem],
    });
  };

  const getConnectionCellClass = (index: number, total: number) => {
    if (index === 0) return `${styles['connection-cell']} ${styles['connection-cell-first']}`;
    if (index === total - 1) return `${styles['connection-cell']} ${styles['connection-cell-last']}`;
    return `${styles['connection-cell']} ${styles['connection-cell-middle']}`;
  };

  const getExpressionCellClass = (index: number, total: number) => {
    if (index === 0) return `${styles['expression-cell']} ${styles['expression-cell-first']}`;
    if (index === total - 1) return `${styles['expression-cell']} ${styles['expression-cell-last']}`;
    return `${styles['expression-cell']} ${styles['expression-cell-middle']}`;
  };

  return (
    <div className="space-y-3">

      {!hasMultipleExpressions && (
        <div className={styles['single-column']}>
          {expressions.length === 0 ? (
            <SimpleExpressionForm
              initialValue={undefined}
              onCancel={() => {}}
              onChange={(expr) => handleUpdateExpression(0, expr)}
              showDelete={false}
              onAddGroup={() => {}}
              showAddGroup={false}
              fieldMetadata={fieldMetadata}
              rightFieldMetadata={rightFieldMetadata}
              operators={operators}
              allowCustomFields={allowCustomFields}
            />
          ) : (
            expressions.map((item, index) =>
              item.expressionGroup ? (
                <div key={index} className={styles['expression-group']}>
                  <RuleGroup
                    group={item.expressionGroup}
                    level={level + 1}
                    onUpdate={(updatedGroup) => {
                      const updatedExpressions = [...expressions];

                      if (updatedGroup.expressions && updatedGroup.expressions.length === 1) {
                        updatedExpressions[index] = updatedGroup.expressions[0];
                      } else {
                        updatedExpressions[index] = {
                          ...updatedExpressions[index],
                          expressionGroup: updatedGroup,
                        };
                      }

                      onUpdate({
                        ...group,
                        expressions: updatedExpressions,
                      });
                    }}
                    fieldMetadata={fieldMetadata}
                    rightFieldMetadata={rightFieldMetadata}
                    operators={operators}
                    allowCustomFields={allowCustomFields}
                  />
                </div>
              ) : (
                <SimpleExpressionForm
                  key={index}
                  initialValue={item.expression || undefined}
                  onCancel={() => handleDeleteExpression(index)}
                  onChange={(expr) => handleUpdateExpression(index, expr)}
                  showDelete={expressions.length > 1}
                  onAddGroup={() => handleCreateNestedGroup(index)}
                  showAddGroup={hasMultipleExpressions && !item.expressionGroup}
                  fieldMetadata={fieldMetadata}
                  rightFieldMetadata={rightFieldMetadata}
                  operators={operators}
                  allowCustomFields={allowCustomFields}
                />
              )
            )
          )}
        </div>
      )}

      {hasMultipleExpressions && (
        <div className={styles.grid}>
          {expressions.map((item, index) => (
            <>
              <div key={`connection-${index}`} className={getConnectionCellClass(index, expressions.length)}>
                {index === 0 && (
                  <div className={styles['logical-operator']}>
                    <div className={styles['not-toggle']}>
                      <SelectButton
                        value={group.isNot ? 'NOT' : ''}
                        options={[{ label: 'NOT', value: 'NOT' }]}
                        onChange={(e) => handleIsNotChange(e.value === 'NOT')}
                        unselectable
                      />
                    </div>
                    <LogicalOperatorToggle value={group.logicalOperator} onChange={handleLogicalOperatorChange} />
                  </div>
                )}
              </div>

              <div key={`expression-${index}`} className={getExpressionCellClass(index, expressions.length)}>
                {item.expressionGroup ? (
                  <div className={styles['expression-group']}>
                    <RuleGroup
                      group={item.expressionGroup}
                      level={level + 1}
                      onUpdate={(updatedGroup) => {
                        const updatedExpressions = [...expressions];

                        if (updatedGroup.expressions && updatedGroup.expressions.length === 1) {
                          updatedExpressions[index] = updatedGroup.expressions[0];
                        } else {
                          updatedExpressions[index] = {
                            ...updatedExpressions[index],
                            expressionGroup: updatedGroup,
                          };
                        }

                        onUpdate({
                          ...group,
                          expressions: updatedExpressions,
                        });
                      }}
                      fieldMetadata={fieldMetadata}
                      rightFieldMetadata={rightFieldMetadata}
                      operators={operators}
                      allowCustomFields={allowCustomFields}
                    />
                  </div>
                ) : (
                  <SimpleExpressionForm
                    initialValue={item.expression || undefined}
                    onCancel={() => handleDeleteExpression(index)}
                    onChange={(expr) => handleUpdateExpression(index, expr)}
                    showDelete={expressions.length > 1}
                    onAddGroup={() => handleCreateNestedGroup(index)}
                    showAddGroup={hasMultipleExpressions && !item.expressionGroup}
                    fieldMetadata={fieldMetadata}
                    rightFieldMetadata={rightFieldMetadata}
                    operators={operators}
                    allowCustomFields={allowCustomFields}
                  />
                )}
              </div>
            </>
          ))}
        </div>
      )}

      <Button
        type="button"
        label="Add Rule"
        icon="pi pi-plus"
        link
        size="small"
        onClick={handleAddRule}
        disabled={hasIncompleteExpression}
        className="font-bold mt-2"
        style={{ width: 'auto', color: hasIncompleteExpression ? '#9ca3af' : undefined }}
      />
    </div>
  );
};
